import { css } from "@emotion/react";
import CommonDialog from "@amplio-common/components/Dialog";
import { Button } from "juno-shared-components";
import Select from "@app/common/components/Select";
import upsideDownImage from "@amplio-common/assets/img/upside_down.png";
import { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { Trans } from "@lingui/macro";
import { useDispatch, useSelector } from "react-redux";
import { selectAudioDeviceId, selectIsVoiceSettingsOpen, selectVideoDevice, setAudioDeviceId, setIsVoiceSettingsOpen, setVideoDevice } from "../../store/videoSlice";
import OT from "@opentok/client";
import { selectIsAudioPermissionGranted, setIsAudioPermissionGranted } from "../../store/techStatusSlice";

const propTypes = {
};

const rootStyle = theme => css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 8px;

  .message-div {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    background-color: #F9E3E9;
    border-radius: 8px;
    width: 100%;
    padding: 12px;
    flex: 1;

    .message-div-title {
      display: flex;
      gap: 6px;
      align-items: center;

      > span {
        font-size: ${theme.typography.subtitle1.fontSize};
        font-weight: ${theme.typography.subtitle1.fontWeight};
      }

      .MuiSvgIcon-root {
        height: 16px;
        width: 16px;
      }
    }

    > button {
      align-self: center;
    }
  }

  .loading-div {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .device-settings {
    align-self: flex-start;
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 12px;

    .title {
      font-size: ${theme.typography.subtitle1.fontSize};
      font-weight: ${theme.typography.subtitle1.fontWeight};
    }
  }

  .sound-test {
    display: flex;
    padding: 8px 16px;
    justify-content: center ;
    gap: 50px;
    align-items: center;

    #volume-visualizer {
    --volume: 0%;
    position: relative;
    width: 50%;
    height: 20px;
    background-color: #DDD;
    border-radius: 16px;
 
    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: var(--volume);
      background-color: ${theme.colors.success.main};
      border-radius: 16px;
      transition: width 100ms linear;
    }
  }

  > .uiButtonBase-root {
      align-self: center;
    }
  }
`;

const dialogStyle = css`
  .MuiDialogActions-root {
    padding: 0 16px 8px;
  }
  
  .MuiDialogContent-root {
    padding-bottom: 0px;
  }
`;
const SessionSettingsDialog = () => {
  const dispatch = useDispatch();

  const volumeCallback = useRef(null);
  const volumeInterval = useRef(null);

  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDevices, setAudioDevices] = useState([]);
  const [isSoundTestOn, setIsSoundTestOn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const currentVideoDevice = useSelector(selectVideoDevice);
  const currentAudioDevice = useSelector(selectAudioDeviceId);
  const isVoiceSettingsOpen = useSelector(selectIsVoiceSettingsOpen);
  const permissionsGranted = useSelector(selectIsAudioPermissionGranted);

  const handleSelectMicrophone = (value) => {
    dispatch(setAudioDeviceId(audioDevices.find(_device => _device.value === value)));
  };

  const handleSelectCamera = (value) => {
    dispatch(setVideoDevice(videoDevices.find(_device => _device.value === value)));
  };

  const stopSoundTest = () => {
    setIsSoundTestOn(false);
    clearInterval(volumeInterval.current);
    volumeInterval.current = null;
  };

  const onCloseSettingsDialog = () => {
    stopSoundTest();
    dispatch(setIsVoiceSettingsOpen(false));
  };
 
  const onSoundTestClick = async () => {
    if (isSoundTestOn) {
      stopSoundTest();
      return;
    }

    setIsSoundTestOn(true);
    if (volumeCallback.current !== null) {
      volumeInterval.current = setInterval(volumeCallback.current, 100);
    }
  };

  const setDevices = async () => {
    setIsLoading(true);
    const cameraPermission = await navigator.permissions.query({ name: "camera" });
    const microphonePermission = await navigator.permissions.query({ name: "microphone" });

    if (cameraPermission.state === "denied" || microphonePermission.state === "denied") {
      return dispatch(setIsAudioPermissionGranted(false));
      // eslint-disable-next-line
    } else {
      dispatch(setIsAudioPermissionGranted(true));
    }
    OT.getDevices((err, devices) => {
      if (err) return;
      const _audioDevices = devices.filter(_device => _device.kind === "audioInput" && _device.deviceId !== "default")
        .map(_device => ({ value: _device.deviceId, label: _device.label }));
      const defaultAudioDevice = devices.find(_device => _device.deviceId);
      const _videoDevices = devices.filter(_device => _device.kind === "videoInput")
        .map(_device => ({ value: _device.deviceId, label: _device.label }));

      setVideoDevices(_videoDevices);
      setAudioDevices(_audioDevices);

      if (!currentVideoDevice && _videoDevices.length > 0) dispatch(setVideoDevice(_videoDevices[0]));
      if (!currentAudioDevice && defaultAudioDevice) {
        dispatch(setAudioDeviceId(_audioDevices.find(_device => defaultAudioDevice.label.includes(_device.label))));
      }
    });
    setIsLoading(false);
  };

  useEffect(() => {
    setDevices();
    return () => isSoundTestOn && stopSoundTest();
  }, []);

  useEffect(async () => {
    if (currentAudioDevice === null) { return; }
    
    if (isVoiceSettingsOpen && isSoundTestOn && currentAudioDevice !== null) {
      stopSoundTest();
      document.getElementById("volume-visualizer")?.style?.setProperty("--volume", "0%");
    }

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: currentAudioDevice.deviceId } });
      const audioContext = new AudioContext();
      const audioSource = audioContext.createMediaStreamSource(audioStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.minDecibels = -127;
      analyser.maxDecibels = 0;
      analyser.smoothingTimeConstant = 0.4;
      audioSource.connect(analyser);
      const volumes = new Uint8Array(analyser.frequencyBinCount);
      volumeCallback.current = () => {
        analyser.getByteFrequencyData(volumes);
        const volumeSum = volumes.reduce((prev, curr) => prev + curr);
        const averageVolume = volumeSum / volumes.length;
        // Value range: 127 = analyser.maxDecibels - analyser.minDecibels;
        document.getElementById("volume-visualizer")?.style?.setProperty("--volume", `${((averageVolume * 100) / 127)}%`); //clear test bar when switching devices
      };
    } catch (e) {
      console.error(e);
    }
  }, [currentAudioDevice, isVoiceSettingsOpen]);

  
  return (
    <>
      <CommonDialog
        title={<Trans>Device settings</Trans>}
        noCloseOnFocusOut
        okLabel={<Trans>Ok</Trans>}
        css={dialogStyle}
        okAction={onCloseSettingsDialog}
        open={isVoiceSettingsOpen}
        maxWidth="sm"
        xAction={onCloseSettingsDialog}
        fullWidth
        container={() => document.getElementById("main-content")}
        BackdropProps={{ style: { position: "absolute" } }}
        style={{ position: "absolute", zIndex: 1402 }}
        disableEnforceFocus
        leftSideAction={(<img src={upsideDownImage} alt="Choose audio device" />)}
        content={(
          <div css={rootStyle}>
            {isLoading && (<div className="loading-div"><CircularProgress size={60} thickness={3} color="primary" /></div>)}
            {!isLoading && permissionsGranted
              ? (
                <>
                  <div className="device-settings">
                    <h3 className="title">
                      <Trans>Microphone</Trans>
                    </h3>
                    <div className="device-selector">
                      <Select
                        label=""
                        options={audioDevices}
                        value={currentAudioDevice?.value || null}
                        onChange={handleSelectMicrophone}
                        placeholder="Current device"
                        isClearable={false}
                        isSearchable={false}
                        variant="blue"
                      />
                    </div>
                    <div className="sound-test">
                      <Button id="test-sound-button" disabled={currentAudioDevice === null} onClick={onSoundTestClick}>{!isSoundTestOn ? <Trans>Test sound</Trans> : <Trans>Stop test</Trans>}</Button>
                      <div id="volume-visualizer" />
                    </div>
                  </div>
                  <div className="device-settings">
                    <h3 className="title">
                      <Trans>Camera</Trans>
                    </h3>
                    <div className="device-selector">
                      <Select
                        label=""
                        options={videoDevices}
                        value={currentVideoDevice?.value || null}
                        onChange={handleSelectCamera}
                        placeholder="Current device"
                        isClearable={false}
                        isSearchable={false}
                        variant="blue"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <p><Trans>Please enable video and audio permissions</Trans></p>
              )}
          </div>
      )}
      />
    </>
  );
};

SessionSettingsDialog.propTypes = propTypes;

export default SessionSettingsDialog;
